import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { LayoutDashboard, Plus, Users, Wallet, ArrowDownLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import { BuyDialog, SellDialog } from "./Dialogs";

// Mobile-only tab bar. Three slots: overview, the app's core write action, and the
// social layer — or, while the community is closed to this account, the holdings,
// which is the next thing a member opens. Hidden from md: up, where the sidebar
// already covers navigation.
export default function BottomNav({ onDone }) {
  const { t } = useTranslation();
  const { features } = useAuth();
  const [sheet, setSheet] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);
  const [sellOpen, setSellOpen] = useState(false);

  const tab = ({ isActive }) =>
    `flex flex-col items-center justify-center gap-1 flex-1 py-2 text-[11px] font-medium transition-colors ${
      isActive ? "text-brand-600" : "text-slate-400"
    }`;

  return (
    <>
      <nav
        data-testid="bottom-nav"
        className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-slate-200
                   flex items-stretch pb-[env(safe-area-inset-bottom)]"
      >
        <NavLink to="/" end className={tab} data-testid="bnav-dashboard">
          <LayoutDashboard size={20} strokeWidth={1.8} />
          {t("nav.dashboard")}
        </NavLink>

        {/* Raised centre action — the thing users open the app to do. */}
        <div className="flex-1 flex justify-center">
          <button
            data-testid="bnav-record"
            onClick={() => setSheet(true)}
            aria-label={t("nav.record")}
            className="-mt-5 w-14 h-14 rounded-full bg-brand-600 text-white shadow-lg shadow-brand-600/30
                       flex items-center justify-center active:scale-95 transition-transform"
          >
            <Plus size={26} strokeWidth={2.2} />
          </button>
        </div>

        {features.community ? (
          <NavLink to="/investors" className={tab} data-testid="bnav-investors">
            <Users size={20} strokeWidth={1.8} />
            {t("nav.investors")}
          </NavLink>
        ) : (
          <NavLink to="/holdings" className={tab} data-testid="bnav-holdings">
            <Wallet size={20} strokeWidth={1.8} />
            {t("nav.holdings")}
          </NavLink>
        )}
      </nav>

      {/* Action sheet: buy or sell, rather than guessing which the tap meant. */}
      {sheet && (
        <div className="md:hidden fixed inset-0 z-50 flex items-end" data-testid="record-sheet">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setSheet(false)} />
          <div className="relative w-full bg-white rounded-t-3xl p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] space-y-3">
            <div className="w-10 h-1 rounded-full bg-slate-200 mx-auto mb-4" />
            <button
              data-testid="sheet-buy"
              className="btn-primary w-full justify-center py-3.5"
              onClick={() => { setSheet(false); setBuyOpen(true); }}
            >
              <Plus size={18} /> {t("common.recordBuy")}
            </button>
            <button
              data-testid="sheet-sell"
              className="btn-outline w-full justify-center py-3.5"
              onClick={() => { setSheet(false); setSellOpen(true); }}
            >
              <ArrowDownLeft size={18} /> {t("common.sell")}
            </button>
            <button className="btn-ghost w-full justify-center" onClick={() => setSheet(false)}>
              {t("common.cancel")}
            </button>
          </div>
        </div>
      )}

      <BuyDialog open={buyOpen} onClose={() => setBuyOpen(false)} onDone={onDone} />
      <SellDialog open={sellOpen} onClose={() => setSellOpen(false)} onDone={onDone} />
    </>
  );
}
